<template>
  <dialog-handler></dialog-handler>
  <gp-toasts-wrapper></gp-toasts-wrapper>
  <router-view></router-view>
</template>

<script setup lang="ts">
import { watch } from "vue";

import config from "./config";
import DialogHandler from "./components/DialogHandler.vue";
import { GpToastsWrapper } from "@gportal/uikit";
import { FeatureFlag, useFeatureFlag } from "./util/useFeatureFlag";
import { bootIntercom } from "./util/useIntercom";

// The messenger needs BOTH `intercom.enabled` and the `support-chat` feature flag, checked in that
// order. They answer different questions and only one of them is free to ask.
//
//   intercom.enabled  should THIS app show a messenger at all? A build-time constant. The panel has
//                     no support page to put a chat on, so no config in this repo sets the key and
//                     the messenger is off everywhere.
//   support-chat      is the messenger rolled out to this user? Owned centrally, shared with the
//                     website, and the lever for turning the whole feature off in a hurry.
//
// Neither subsumes the other. The evaluation context (featureflags/context.ts) carries
// targetingKey, environment, region, groups and country — plenty to target a rollout by, and worth
// using — but nothing that says WHICH application is asking, so the flag cannot mean "website yes,
// panel no". And the config key must not be able to bypass a rollout kill switch. Turning the panel
// messenger on therefore means setting `intercom.enabled` AND having the flag.
//
// Config first, and not merely for tidiness: with the key absent, which is every environment today,
// nothing subscribes to the flag and the OpenFeature provider is never consulted for this at all.
if (config.intercom.enabled) {
  // Watched rather than read once: the flag is a ComputedRef that flips when the OpenFeature
  // provider becomes ready. bootIntercom is idempotent and guards on auth itself.
  const { hasAccessToFeature } = useFeatureFlag();
  const supportChatEnabled = hasAccessToFeature(FeatureFlag.SupportChat);

  watch(
    supportChatEnabled,
    (enabled) => {
      if (enabled) {
        void bootIntercom();
      }
    },
    { immediate: true },
  );
}
</script>
